import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

/** ===================== Interfaces ===================== **/

/** Single GST Slab object (must match API response) */
export interface GstSlab {
  id: string; // UUID
  value: Number;
  category: string;
  displayLabel: string;
  description: string;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  gstUom: string | null;
  purchaseGstOn: string | null;
  _auditContext?: any;
}

/** Paginated response */
export interface GstSlabsResponse {
  data: GstSlab[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/** DTO for creating a slab */
export type CreateGstSlabDto = Omit<
  GstSlab,
  | 'id'
  | 'isActive'
  | 'createdAt'
  | 'updatedAt'
  | 'gstUom'
  | 'purchaseGstOn'
  | '_auditContext'
>;

/** DTO for updating a slab */
export type UpdateGstSlabDto = Partial<
  Omit<
    GstSlab,
    'id' | 'createdAt' | 'updatedAt' | '_auditContext' | 'sortOrder'
  >
>;

/** ===================== Service ===================== **/

@Injectable({
  providedIn: 'root',
})
export class GstSlabService {
  private apiUrl = `${environment.apiUrl}/gstslabs`; // ✅ clean base URL

  private httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
    }),
  };

  constructor(private http: HttpClient) {}

  /**
   * Create a new GST slab
   */
  createGstSlab(payload: CreateGstSlabDto): Observable<GstSlab> {
    return this.http.post<GstSlab>(this.apiUrl, payload, this.httpOptions);
  }

  /**
   * Get all GST slabs with optional pagination & search
   * @param page Page number (default 1)
   * @param limit Page size (default 10)
   * @param search Optional search string
   */
  getAllGstSlabs(
    page: number = 1,
    limit: number = 10,
    search: string = ''
  ): Observable<GstSlabsResponse> {
    let params = new HttpParams().set('page', page).set('limit', limit);
    if (search.trim()) {
      params = params.set('search', search.trim());
    }
    return this.http.get<GstSlabsResponse>(this.apiUrl, { params });
  }

  /**
   * Get a single GST slab by ID
   */
  getGstSlabById(id: string): Observable<GstSlab> {
    return this.http.get<GstSlab>(`${this.apiUrl}/${id}`);
  }

  /**
   * Update an existing GST slab
   */
  updateGstSlab(id: string, payload: UpdateGstSlabDto): Observable<GstSlab> {
    return this.http.patch<GstSlab>(
      `${this.apiUrl}/${id}`,
      payload,
      this.httpOptions
    );
  }

  /**
   * Delete / deactivate a GST slab
   */
  deleteGstSlab(id: string): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(`${this.apiUrl}/${id}`);
  }
}
